import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';
import {
  Button,
  Error,
  loadingToast,
  Spinner,
  SpinnerWrapper,
  TableActionHeader,
  TToastType,
  updateToast,
} from '@sim-admin-frontends/ui-shared';
import {
  useInfiniteInfluencerSpotsQuery,
  useSaDeleteInfluencerSpotMutation,
} from '@sim-admin-frontends/data-access';
import { useTablePaging } from '@sim-admin-frontends/utils-shared';

import ROUTES from '../../../routing/routes';
import InfluencerSpotsView from './InfluencerSpotsView';
import { createTableColumns } from './helper';
import { INFLUENCER_TOAST_ID } from '../../../constants/Constants';

type Props = {
  influencerId: string;
};

const InfluencerSpotsViewContainer = ({ influencerId }: Props) => {
  const { t } = useTranslation();

  const { data, isLoading, isError, fetchNextPage, hasNextPage, refetch, isFetchingNextPage } =
    useInfiniteInfluencerSpotsQuery({ influencerUuid: influencerId });

  const { mutate: deleteMutation } = useSaDeleteInfluencerSpotMutation({
    onMutate: () => {
      loadingToast(t('institutions.deleteToast.loading'), {
        toastId: INFLUENCER_TOAST_ID,
      });
    },
    onError: () => {
      updateToast(INFLUENCER_TOAST_ID, t('institutions.deleteToast.error'), TToastType.ERROR);
    },
    onSuccess: () => {
      updateToast(INFLUENCER_TOAST_ID, t('institutions.deleteToast.success'), TToastType.SUCCESS);
      refetch();
    },
  });

  const columns = createTableColumns({ t, refetch, deleteMutation, influencerId });

  const { currentPageIndex, onPreviousPress, onNextPress } = useTablePaging(
    fetchNextPage,
    hasNextPage,
  );

  if (isLoading) {
    return (
      <SpinnerWrapper>
        <Spinner />
      </SpinnerWrapper>
    );
  }

  if (isError) {
    return (
      <>
        <TableActionHeader>
          <Link to={generatePath(ROUTES.influencerSpotsEdit.path, { influencerId })}>
            <Button>{t('influencers.addInfluencer')}</Button>
          </Link>
        </TableActionHeader>
        <SpinnerWrapper>
          <Error caption={t('error.fetchingDataError')} onClick={refetch} />
        </SpinnerWrapper>
      </>
    );
  }

  const itemsCount = data?.pages?.[0].influencerSpotsBy?.influencerSpots?.length || 0;
  const influencerSpotsData =
    data?.pages?.[currentPageIndex]?.influencerSpotsBy?.influencerSpots || [];
  const hasNextToken = !!data?.pages[currentPageIndex].influencerSpotsBy.nextToken;

  return (
    <>
      <TableActionHeader>
        <Link to={generatePath(ROUTES.influencerSpotsEdit.path, { influencerId })}>
          <Button testId="InfluencerSpotsTabs#AddInfluencerSpot">
            {t('influencerSpos.addInfluencerSpot')}
          </Button>
        </Link>
      </TableActionHeader>
      <InfluencerSpotsView
        loading={isLoading || isFetchingNextPage}
        itemsCount={itemsCount as number}
        pageIndex={currentPageIndex}
        onNextPress={onNextPress}
        onPreviousPress={onPreviousPress}
        hasNextPage={hasNextToken ?? (hasNextPage || false)}
        columns={columns}
        data={influencerSpotsData || []}
      />
    </>
  );
};

export default InfluencerSpotsViewContainer;
